import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import useSupabaseClient from '../hooks/useSupabaseClient';
import InvoiceAccountingExampleEditorModal from './InvoiceAccountingExampleEditorModal';
import { isDemoAccount } from '../lib/demoAccount.mjs';

const InvoiceAccountingExamples = () => {
  const { supabase } = useSupabaseClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [examples, setExamples] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [currentExample, setCurrentExample] = useState(null);

  const storedUser = localStorage.getItem('invoice_user');
  const userId = storedUser ? (() => {
    try {
      const u = JSON.parse(storedUser);
      return u?.id || null;
    } catch {
      return null;
    }
  })() : null;

  const fetchExamples = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      if (!userId) throw new Error('Usuario no autenticado');

      const { data, error: qErr } = await supabase
        .from('invoice_accounting_examples')
        .select('*')
        .eq('user_id', userId)
        .order('updated_at', { ascending: false });

      if (qErr) throw qErr;
      setExamples(data || []);
    } catch (e) {
      setError(e.message || 'Error al cargar');
      setExamples([]);
    } finally {
      setIsLoading(false);
    }
  }, [supabase, userId]);

  useEffect(() => {
    fetchExamples();
  }, [fetchExamples]);

  const handleNew = () => {
    setCurrentExample(null);
    setEditorOpen(true);
  };

  const handleEdit = (ex) => {
    setCurrentExample(ex);
    setEditorOpen(true);
  };

  const handleDelete = async (ex) => {
    if (!window.confirm('¿Eliminar este ejemplo?')) return;
    setIsLoading(true);
    try {
      if (!userId) throw new Error('Usuario no autenticado');

      const { error: delErr } = await supabase
        .from('invoice_accounting_examples')
        .delete()
        .eq('id', ex.id)
        .eq('user_id', userId);

      if (delErr) throw delErr;
      setExamples(prev => prev.filter(x => x.id !== ex.id));
    } catch (e) {
      setError(e.message || 'Error al eliminar');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" color="primary" fontWeight={600}>
            Ejemplos Contables (IA)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Crea ejemplos explicados y reutiliza asientos existentes para entrenar tu IA.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchExamples} disabled={isLoading}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleNew} disabled={isLoading}>
            Nuevo ejemplo
          </Button>
        </Box>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" height="240px">
          <CircularProgress />
        </Box>
      )}

      {!isLoading && error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {!isLoading && !error && examples.length === 0 && (
        <Typography color="text.secondary">
          No tienes ejemplos todavía. Crea el primero con “Nuevo ejemplo”.
        </Typography>
      )}

      {!isLoading && examples.length > 0 && (
        <Grid container spacing={2}>
          {examples.map(ex => (
            <Grid item xs={12} md={6} lg={4} key={ex.id}>
              <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flex: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {ex.title}
                  </Typography>
                  {ex.tags && Array.isArray(ex.tags) && ex.tags.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {isDemoAccount(userId) ? 'Etiquetas' : 'Tags'}: {ex.tags.join(', ')}
                    </Typography>
                  )}
                  {ex.reason && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {ex.reason}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end' }}>
                  <IconButton onClick={() => handleEdit(ex)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(ex)}>
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <InvoiceAccountingExampleEditorModal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        example={currentExample}
        userId={userId}
        onSaved={() => fetchExamples()}
      />
    </Box>
  );
};

export default InvoiceAccountingExamples;
